// Portaled bottom panel for Fleet Map: closed by default; FAB opens, header closes.

import React, { useEffect, useState, useRef, type JSX } from 'react';
import { Map as MapIcon, X } from 'lucide-react';

import FleetMapPage from '../../pages/FleetMapPage';
import { PortaledLayer } from '../shared/PortaledLayer';
import { useMapDock } from '../../context/MapDockContext';

const TAB_BAR_PX = 60;

import type { GlobalMapDockProps } from './types/MapDockProps';

export type { GlobalMapDockProps };

export default function GlobalMapDock({
  operatorScopedEventId   = null,
  operatorScopedEventName = null,
  operatorScopedRaceId    = null,
}: GlobalMapDockProps): JSX.Element {
  const { mapDockOpen, setMapDockOpen } = useMapDock();
  const layoutTickRef = useRef(0);
  const [layoutTick, setLayoutTick] = useState(0);

  useEffect(() => {
    const bump = () => { layoutTickRef.current += 1; setLayoutTick(layoutTickRef.current); };
    window.addEventListener('resize', bump);
    return () => window.removeEventListener('resize', bump);
  }, []);

  useEffect(() => {
    if (!mapDockOpen) return;
    layoutTickRef.current += 1;
    setLayoutTick(layoutTickRef.current);
  }, [mapDockOpen]);

  return (
    <PortaledLayer>
      {!mapDockOpen ? (
        <div
          className="fixed z-[220] motion-safe:transition-transform motion-safe:duration-300"
          style={{ bottom: TAB_BAR_PX + 14, right: 16 }}
        >
          <button
            type="button"
            onClick={() => setMapDockOpen(true)}
            className="group flex h-13 w-13 items-center justify-center rounded-2xl text-white transition-all hover:scale-105 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100"
            style={{
              width: 52, height: 52,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 4px 20px rgba(59,130,246,0.45), 0 0 0 1px rgba(255,255,255,0.12) inset',
            }}
            aria-label="Open Fleet Map"
          >
            <MapIcon className="h-6 w-6 transition motion-safe:group-hover:rotate-[-6deg]" />
          </button>
        </div>
      ) : (
        <div
          className="fixed left-2 right-2 z-[200] flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900 dark:shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.6)]"
          style={{
            bottom: TAB_BAR_PX,
            height: 'min(92vh, calc(100vh - 3.5rem))',
          }}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-slate-600">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50">
                <MapIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-gray-900 dark:text-slate-200">Fleet Map</div>
                <div className="truncate text-[10px] text-gray-500 dark:text-slate-400">Radius filter · live positions</div>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500 transition-colors hover:text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
              onClick={() => setMapDockOpen(false)}
              aria-label="Close Fleet Map"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            <FleetMapPage
              variant="dock"
              operatorScopedEventId={operatorScopedEventId}
              operatorScopedEventName={operatorScopedEventName}
              operatorScopedRaceId={operatorScopedRaceId}
              layoutRevision={layoutTick}
            />
          </div>
        </div>
      )}
    </PortaledLayer>
  );
}
